import { $createListNode, $isListItemNode, $isListNode, $removeList, ListItemNode } from '@lexical/list';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $createParagraphNode, $getSelection, $isLeafNode, $isRangeSelection, LexicalNode } from 'lexical';

const getSelectedListItems = (): ListItemNode[] => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return [];
  }

  // an empty list item holds no leaf node, so the selection is on the item itself
  const anchorNode = selection.anchor.getNode();
  if ($isListItemNode(anchorNode)) {
    return [anchorNode];
  }

  // getNodes() is in document order, which the conversion below relies on
  const listItems = new Set<ListItemNode>();
  for (const node of selection.getNodes()) {
    if (!$isLeafNode(node)) {
      continue;
    }
    const listItem = $getNearestNodeOfType(node, ListItemNode);
    if (listItem) {
      listItems.add(listItem);
    }
  }
  return [...listItems];
};

/**
 * Converts the list items covered by the current selection into paragraphs and keeps the remaining
 * items of the list, splitting the list when the selection is in the middle of it.
 * `$removeList()` from `@lexical/list` always converts the complete list instead.
 */
export const $removeSelectedListItems = (): void => {
  const listItems = getSelectedListItems();

  // a nested list is a child of a list item, which cannot hold the paragraph that replaces one of its
  // items. Such lists can only come from stored content, as the editor does not offer indenting, so
  // converting the whole list like `$removeList()` does is the predictable fallback here.
  const isNested = listItems.some((listItem) => {
    const list = listItem.getParent();
    return $isListNode(list) && $isListItemNode(list.getParent());
  });
  if (isNested) {
    $removeList();
    return;
  }

  // the items are converted from the last to the first one, so that splitting off the items after an
  // item cannot interfere with the items that are still to be converted
  for (const listItem of listItems.reverse()) {
    const list = listItem.getParent();
    if (!$isListNode(list)) {
      continue;
    }

    // keep the items after the converted one in a list of their own, which starts counting from 1 again
    const followingItems = listItem.getNextSiblings();
    if (followingItems.length) {
      const followingList = $createListNode(list.getListType());
      followingList.append(...followingItems);
      list.insertAfter(followingList);
    }

    // a nested list cannot be part of a paragraph and is moved to the level of the new paragraph
    const paragraph = $createParagraphNode();
    const nestedLists: LexicalNode[] = [];
    for (const child of listItem.getChildren()) {
      if ($isListNode(child)) {
        nestedLists.push(child);
      } else {
        paragraph.append(child);
      }
    }

    list.insertAfter(paragraph);
    let lastInserted: LexicalNode = paragraph;
    for (const nestedList of nestedLists) {
      lastInserted.insertAfter(nestedList);
      lastInserted = nestedList;
    }

    // an empty item carries the selection itself, which has to move to the new paragraph
    if ($getSelection()?.getNodes().includes(listItem)) {
      paragraph.select(0, 0);
    }

    listItem.remove();
    if (!list.getChildrenSize()) {
      list.remove();
    }
  }
};
